import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';
import type IdsSplitter from './ids-splitter';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { PADDING_SIZES } from '../ids-layout-grid/ids-layout-grid-common';

import styles from './ids-splitter-pane.scss';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS SplitterPane Component
 * @type {IdsSplitterPane}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part pane - the splitter pane container element
 */
@customElement('ids-splitter-pane')
@scss(styles)
export default class IdsSplitterPane extends Base {
  constructor() {
    super();

    // Initialize state object first, before any property access
    this.state = {
      collapsedSize: '',
      expandSize: 0,
      currentSize: 0,
      computedMinSize: 0,
      computedMaxSize: 0,
      isPixelSize: false,
      pixelSizeValue: 0
    };
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.COLLAPSED,
      attributes.NO_SCROLL,
      attributes.MIN_SIZE,
      attributes.MAX_SIZE,
      attributes.SIZE,
      attributes.PADDING,
      attributes.PADDING_X,
      attributes.PADDING_Y
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    return `<div class="ids-splitter-pane" part="pane"><slot></slot></div>`;
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();

    if (this.size) {
      this.state.collapsedSize = String(this.size);
    }
    this.#attachEventHandlers();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.offEvent('scroll.splitter-pane');
  }

  /**
   * Internal state for the pane
   * @private
   */
  state: {
    collapsedSize: string;
    expandSize: number;
    currentSize: number;
    computedMinSize: number;
    computedMaxSize: number;
    /** Whether the pane's size was originally specified in pixels */
    isPixelSize: boolean;
    /** The original pixel value when size is pixel-based */
    pixelSizeValue: number;
  };

  /**
   * Get the current size as a number (percentage).
   * For pixel-based panes, returns the internal percentage equivalent.
   * @returns {number} Current size percentage
   */
  getSizeAsNumber(): number {
    // For pixel-based panes, use the internal percentage state
    // since the attribute contains px values
    if (this.state.isPixelSize) {
      return this.state.currentSize || 0;
    }
    const sizeStr = this.getAttribute(attributes.SIZE);
    if (!sizeStr) return this.state.currentSize || 0;
    return parseFloat(sizeStr);
  }

  /**
   * Check if this pane has a pixel-based size
   * @returns {boolean} True if the pane size is in pixels
   */
  isPixelBased(): boolean {
    return this.state.isPixelSize === true;
  }

  /**
   * Get the original pixel size value (only meaningful when isPixelBased() is true)
   * @returns {number} The pixel size value
   */
  getPixelSizeValue(): number {
    return this.state.pixelSizeValue || 0;
  }

  /**
   * Get the min size as a number (percentage)
   * @returns {number} Minimum size percentage
   */
  getMinSizeAsNumber(): number {
    const minSizeStr = this.getAttribute(attributes.MIN_SIZE);
    if (!minSizeStr) return 0;
    return parseFloat(minSizeStr);
  }

  /**
   * Get the max size as a number (percentage)
   * @returns {number | null} Maximum size percentage or null if not set
   */
  getMaxSizeAsNumber(): number | null {
    const maxSizeStr = this.getAttribute(attributes.MAX_SIZE);
    if (!maxSizeStr) return null;
    return parseFloat(maxSizeStr);
  }

  /**
   * Save the current size for later expansion
   * @param {number} size The size to save
   */
  saveExpandSize(size: number): void {
    this.state.expandSize = size;
  }

  /**
   * Get the saved expand size
   * @returns {number} The saved expand size
   */
  getExpandSize(): number {
    return this.state.expandSize;
  }

  /**
   * Attach event handlers
   * @private
   */
  #attachEventHandlers() {
    this.offEvent('scroll.splitter-pane');
    this.onEvent('scroll.splitter-pane', this.shadowRoot?.querySelector('.ids-splitter-pane'), () => {
      this.triggerEvent('scroll', this, {
        bubbles: true,
        detail: {
          elem: this
        }
      });
    });
  }

  /**
   * Set the pane size (width or height depending on axis)
   * @param {string} value for the size in percent with the percent sign
   */
  set size(value: number | string | null) {
    if (value) {
      const sizeStr = String(value);
      this.setAttribute(attributes.SIZE, sizeStr || `${100 / (this.parentNode?.childElementCount || 0)}%`);
      this.state.currentSize = parseFloat(sizeStr);
    } else {
      this.removeAttribute(attributes.SIZE);
      this.state.currentSize = 0;
    }

    const parent = this.parentNode as IdsSplitter;
    if (parent && parent.initialized && !parent.isRefreshing) {
      parent.refreshSizes(this);
    }
  }

  /**
   * Get the current panes size
   * @returns {number | string | null} the size value
   */
  get size(): number | string | null {
    const existingSize = this.getAttribute(attributes.SIZE);
    if (existingSize) return existingSize;

    const siblings = Array.from(this.parentNode?.children || []) as IdsSplitterPane[];
    const totalUsed = siblings.reduce((sum, pane) => {
      const sizeVal = pane.getAttribute(attributes.SIZE);
      return sum + (sizeVal ? parseFloat(sizeVal) : 0);
    }, 0);
    const elementsWithoutSize = siblings.filter((pane) => !pane.getAttribute(attributes.SIZE)).length;
    return elementsWithoutSize > 0 ? `${(100 - totalUsed) / elementsWithoutSize}%` : '0%';
  }

  /**
   * Set the collapsed state of the pane
   * @param {boolean} value for the size in percent with the percent sign
   */
  set collapsed(value: boolean) {
    const val = stringToBool(value);
    const previousVal = stringToBool(this.collapsed);
    if (val === previousVal) return;

    // Save collapsedSize before collapsing (but don't manually set size on expand)
    if (val) {
      this.state.collapsedSize = String(this.size) === '0%' ? this.state.collapsedSize : String(this.size);
    }

    if (this.parentNode && (this.parentNode as IdsSplitter).initialized) {
      const splitter = this.parentNode as IdsSplitter;
      const panes = splitter.splitterPanes;
      const currentIndex = panes.indexOf(this);

      if (currentIndex !== -1) {
        const adjacentPane = currentIndex === 0 ? panes[1] : panes[currentIndex - 1];
        if (adjacentPane) {
          const startPane = `#${this.id}`;
          const endPane = `#${adjacentPane.id}`;

          if (val) {
            splitter.collapse({ startPane, endPane });
          } else {
            splitter.expand({ startPane, endPane });
          }
        }
      }
    } else {
      // Parent not yet initialized — set the attribute so #setInitialCollapsed() picks it up
      this.toggleAttribute(attributes.COLLAPSED, val);
    }
  }

  /**
   * Get collapsed state of the pane
   * @returns {boolean} the size value
   */
  get collapsed(): boolean {
    return stringToBool(this.getAttribute(attributes.COLLAPSED));
  }

  /**
   * Set the pane minSize
   * @param {string} value for the minSize
   */
  set minSize(value: number | string | null) {
    if (value) {
      this.setAttribute(attributes.MIN_SIZE, String(value));
    } else {
      this.removeAttribute(attributes.MIN_SIZE);
    }

    if (this.parentNode && (this.parentNode as IdsSplitter).initialized) {
      (this.parentNode as IdsSplitter).refreshSizes();
    }
  }

  /**
   * Get the current panes minSize
   * @returns {number | string | null} the minSize value
   */
  get minSize(): number | string | null {
    return this.getAttribute(attributes.MIN_SIZE);
  }

  /**
   * Set the pane maxSize
   * @param {string} value for the maxSize
   */
  set maxSize(value: number | string | null) {
    if (value) {
      this.setAttribute(attributes.MAX_SIZE, String(value));
    } else {
      this.removeAttribute(attributes.MAX_SIZE);
    }

    if (this.parentNode && (this.parentNode as IdsSplitter).initialized) {
      (this.parentNode as IdsSplitter).refreshSizes();
    }
  }

  /**
   * Get the current panes maxSize
   * @returns {number | string | null} the maxSize value
   */
  get maxSize(): number | string | null {
    return this.getAttribute(attributes.MAX_SIZE);
  }

  /**
   * Set the noScroll property to disable scrollbars
   * @param {boolean} val for the noScroll property
   */
  set noScroll(val: boolean) {
    const hideScroll = stringToBool(val);
    this.toggleAttribute(attributes.NO_SCROLL, hideScroll);
    this.container?.style.setProperty('overflow', hideScroll ? 'hidden' : 'auto');
  }

  get noScroll(): boolean {
    return stringToBool(attributes.NO_SCROLL);
  }

  /**
   * Set the padding attribute
   * @param {string | null} value The value of the padding attribute
   */
  set padding(value: string | null) {
    if (!value || PADDING_SIZES.indexOf(value) === -1) {
      this.removeAttribute(attributes.PADDING);
    } else {
      this.setAttribute(attributes.PADDING, value);
    }
  }

  /**
   * Get the padding attribute
   * @returns {string | null} The padding value
   */
  get padding(): string | null { return this.getAttribute(attributes.PADDING); }

  /**
   * Set the paddingX attribute
   * @param {string | null} value The value of the paddingX attribute
   */
  set paddingX(value: string | null) {
    if (!value || PADDING_SIZES.indexOf(value) === -1) {
      this.removeAttribute(attributes.PADDING_X);
    } else {
      this.setAttribute(attributes.PADDING_X, value);
    }
  }

  /**
   * Get the paddingX attribute
   * @returns {string | null} The paddingX value
   */
  get paddingX(): string | null { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the paddingY attribute
   * @param {string | null} value The value of the paddingY attribute
   */
  set paddingY(value: string | null) {
    if (!value || PADDING_SIZES.indexOf(value) === -1) {
      this.removeAttribute(attributes.PADDING_Y);
    } else {
      this.setAttribute(attributes.PADDING_Y, value);
    }
  }

  /**
   * Get the paddingY attribute
   * @returns {string | null} The paddingY value
   */
  get paddingY(): string | null { return this.getAttribute(attributes.PADDING_Y); }
}
